/**
 * brand.js — AOS Canonical Brand Configuration
 * AOS-BRAND-SYNC-01 | Version 1.0.0 | 2026-11-01
 *
 * Single source of truth for application brand identity.
 * Sourced from: academiaos.ai public portal (verified 2026-11-01)
 *
 * GOVERNANCE:
 *   - Changes to this file require AOS-BRAND-SYNC-xx approval.
 *   - Do not derive marketing claims from internal build metrics.
 *   - Capability readiness MUST use the AVAILABLE / PLANNED / ROADMAP
 *     classification system. Do not claim ROADMAP features as live.
 *   - This file is independently deployable from the public portal.
 *     It is NOT a runtime dependency on academiaos.ai.
 */

// ── Identity ──────────────────────────────────────────────────────────────────
export const BRAND = {
  version: "1.0.0",
  releaseDate: "2026-11-01",

  product: "AcademiaOS",
  /** Rendered as lowercase 'a' + uppercase 'OS' per portal typography */
  productRendered: "academiaOS",
  category: "University Operating System",

  // ── Approved messaging ────────────────────────────────────────────────────
  headlines: {
    /** Primary campaign headline — approved for login and portal hero */
    campaign: "Education runs better together.",
    /** Accent word within the campaign headline (displayed in brand purple) */
    campaignAccentWord: "together.",
    /** Sub-section on institutional connectivity */
    platform: "One connected operating system. Every academic moment.",
  },

  statements: {
    /** Supporting statement — approved for login and portal hero */
    supporting:
      "Connect academics, student journeys, people, finance and campus life through one governed university operating system.",
    /** Trust / footer statement */
    trust: "Built for education. Governed for institutions.",
    /** Portal footer line */
    footer: "One intelligent university operating system. Built for education, governed for institutions.",
  },

  // ── Eyebrow labels ────────────────────────────────────────────────────────
  eyebrows: {
    hero: "BUILT FOR THE WHOLE INSTITUTION",
    platform: "INTRODUCING A DIFFERENT KIND OF PLATFORM",
  },

  // ── Three canonical pillars (portal-verified) ─────────────────────────────
  pillars: [
    {
      label: "Connected by design",
      description: "People, academics, campus and finance share one canonical record — not twelve islands.",
    },
    {
      label: "Governed for institutions",
      description: "Permission-aware access, institutional authority and auditability across every domain.",
    },
    {
      label: "Built to scale",
      description: "From a single college to a group of institutions, without fragmenting the platform.",
    },
  ],

  // ── Capability readiness classification ───────────────────────────────────
  readiness: {
    AVAILABLE: "AVAILABLE",
    PLANNED:   "PLANNED",
    ROADMAP:   "ROADMAP",
  },

  // ── 12 Canonical institutional domains (portal-verified order) ────────────
  domains: [
    { id: 1,  label: "Home & Work" },
    { id: 2,  label: "Academics" },
    { id: 3,  label: "Student Lifecycle" },
    { id: 4,  label: "Assessment" },
    { id: 5,  label: "Campus Services" },
    { id: 6,  label: "Operations" },
    { id: 7,  label: "Finance" },
    { id: 8,  label: "People" },
    { id: 9,  label: "Research" },
    { id: 10, label: "Quality" },
    { id: 11, label: "Intelligence" },
    { id: 12, label: "Administration" },
  ],

  // ── Target institution categories (portal-verified) ───────────────────────
  institutionCategories: ["Universities", "Colleges", "Institution Groups", "Schools"],

  // ── AI governance language (portal-verified) ─────────────────────────────
  aiGovernance: {
    headline: "AI that works with people, not around them.",
    description:
      "Contextual, measurable and governed. Designed for role-safe assistance, transparent approvals and cost-effective model selection — not uncontrolled agent swarms.",
    capabilities: [
      { label: "Institutional Intelligence", readiness: "AVAILABLE" },
      { label: "Role-safe AI assistance",    readiness: "PLANNED" },
      { label: "Governed agents",            readiness: "ROADMAP",
        note: "Not live or production-certified." },
    ],
  },

  // ── Legal (portal footer) ─────────────────────────────────────────────────
  legal: {
    copyright: "© 2026 AcademiaOS.",
    links: ["Legal Notice", "Terms", "Privacy", "Cookies", "Disclaimer", "Acceptable Use", "Accessibility"],
  },
};

// ── Discrepancy record — Login.jsx v1 vs Brand v1.0.0 ─────────────────────────
/**
 * AOS-BRAND-SYNC-01 Discrepancy Matrix (corrected in Login.jsx v2)
 *
 * ┌─────────────────────────────────┬───────────────────────────────────────┬──────────────────────────────────────────────┐
 * │ Location                        │ Previous (incorrect)                  │ Canonical (portal-verified)                  │
 * ├─────────────────────────────────┼───────────────────────────────────────┼──────────────────────────────────────────────┤
 * │ Logo sub-label                  │ "Unified Academic Intelligence"       │ "University Operating System"                │
 * │ Left hero eyebrow               │ "The Operating System for the         │ "BUILT FOR THE WHOLE INSTITUTION"            │
 * │                                 │  AI-Native University"                │                                              │
 * │ Left hero headline              │ "One operating system for every       │ "Education runs better together."            │
 * │                                 │  academy, university and government   │ ("together." accented in brand purple)       │
 * │                                 │  training authority."                 │                                              │
 * │ Left supporting copy            │ "Configure institutions, deploy 8 AI  │ Approved supporting statement                │
 * │                                 │  modules with human-in-the-loop       │                                              │
 * │                                 │  governance, and switch tenants —     │                                              │
 * │                                 │  ISB, EAIC, or Bradford — in a        │                                              │
 * │                                 │  single click."                       │                                              │
 * │ Left stat: 15 User roles        │ Internal engineering metric           │ REMOVED — not a customer benefit             │
 * │ Left stat: 8 AI modules         │ Unverified / internal                 │ REMOVED                                      │
 * │ Left stat: 7 Build phases       │ Internal build tracking data          │ REMOVED                                      │
 * │ Right panel SSO claim           │ "Enterprise SSO ready · JWT for demo" │ REMOVED — unverified claim                   │
 * │ Right panel subtitle            │ "The Operating System for the         │ "University Operating System"                │
 * │                                 │  AI-Native University"                │                                              │
 * │ Form default pre-fill           │ admin@academiaos.ai + password        │ Empty — credentials must be entered          │
 * ├─────────────────────────────────┼───────────────────────────────────────┼──────────────────────────────────────────────┤
 * │ UNCHANGED (verified OK)         │                                       │                                              │
 * │ Login form structure            │ Email + password + Google SSO         │ Preserved as-is                              │
 * │ Demo account buttons            │ 4 demo accounts                       │ Preserved (authorized demo environment)      │
 * │ Error handling                  │ Classified HTTP errors                │ Preserved                                    │
 * │ App Shell                       │ No change                             │ No change                                    │
 * └─────────────────────────────────┴───────────────────────────────────────┴──────────────────────────────────────────────┘
 *
 * REMAINING DISCREPANCIES REQUIRING OWNER APPROVAL:
 *   1. Demo account password embedding — passwords still present in DEMO[] array.
 *      Resolution options: (a) accept as demo-environment-only; (b) implement
 *      a server-side demo-login API that issues tokens without client-side secrets.
 *   2. Google SSO button — "Continue with Google" is an authorized Emergent-managed
 *      integration. Its availability in production should be confirmed before launch.
 *   3. Domain nav labels in Sidebar.jsx (12 domains ✅ match portal).
 *      Structural items within domains are outside BRAND-SYNC-01 scope.
 */
export const DISCREPANCY_MATRIX_VERSION = "1.0.0";
